import type { TeamRegistrationField } from '@/types/tournament/game';
import type { PhoneData } from '@/types/tournament/quickRegistration';

/**
 * Frontend validation for team registration fields
 * Checks required, length, and basic format validation (EMAIL, NUMBER_ONLY, CHAR_ONLY, REGEX)
 * Backend performs additional validation as the source of truth
 * @param field - The field configuration
 * @param value - The value to validate
 * @param isMandatory - Whether the field is mandatory (from roleValidationRules)
 */
export const validateFieldValue = (
  field: TeamRegistrationField,
  value: string | PhoneData | null | undefined,
  isMandatory: boolean,
): { isValid: boolean; error?: string } => {
  // Convert value to string for validation
  let stringValue = '';
  if (value === null || value === undefined) {
    stringValue = '';
  } else if (typeof value === 'object' && 'number' in value) {
    // For phone objects, use the number field
    stringValue = String(value.number || '').trim();
  } else {
    stringValue = String(value).trim();
  }

  // Check if field is mandatory
  if (isMandatory && !stringValue) {
    return {
      isValid: false,
      error: `${field.fieldLabel} is required`,
    };
  }

  // If field is not mandatory and value is empty, it's valid
  if (!isMandatory && !stringValue) {
    return { isValid: true };
  }

  // Check length constraints
  const length = stringValue.length;

  if (field.minLength !== undefined && length < field.minLength) {
    return {
      isValid: false,
      error: `${field.fieldLabel} must be at least ${field.minLength} characters`,
    };
  }

  if (field.maxLength !== undefined && length > field.maxLength) {
    return {
      isValid: false,
      error: `${field.fieldLabel} cannot exceed ${field.maxLength} characters`,
    };
  }

  // Format validation checks
  if (field.inputType === 'EMAIL' && stringValue) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(stringValue)) {
      return {
        isValid: false,
        error: `${field.fieldLabel} must be a valid email address`,
      };
    }
  }

  if (field.inputType === 'NUMBER_ONLY' && stringValue) {
    const numberRegex = /^\d+$/;
    if (!numberRegex.test(stringValue)) {
      return {
        isValid: false,
        error: `${field.fieldLabel} must contain only numbers`,
      };
    }
  }

  if (field.inputType === 'CHAR_ONLY' && stringValue) {
    const charRegex = /^[a-zA-Z\s]+$/;
    if (!charRegex.test(stringValue)) {
      return {
        isValid: false,
        error: `${field.fieldLabel} must contain only letters`,
      };
    }
  }

  if (field.inputType === 'REGEX' && stringValue && field.regex) {
    try {
      const regexPattern = new RegExp(field.regex);
      if (!regexPattern.test(stringValue)) {
        // Use custom errorMessage if provided, otherwise default message
        const errorMsg =
          field.errorMessage || `${field.fieldLabel} format is invalid`;
        return {
          isValid: false,
          error: errorMsg,
        };
      }
    } catch (error) {
      console.warn(
        `Invalid regex pattern for field ${field.fieldKey}: ${field.regex}`,
        error,
      );
      // Return valid to avoid blocking, backend will catch invalid regex
      return { isValid: true };
    }
  }

  return { isValid: true };
};
